/**
 * NAS 경로 변환 유틸리티
 * Firebase Storage 경로를 NAS 경로로 변환하는 헬퍼 함수들
 */

/**
 * 안전한 폴더명 생성
 * - 특수문자 제거
 * - 공백을 언더스코어로 변환
 * - 한글, 영문, 숫자, 하이픈, 언더스코어만 허용
 * - 최대 길이 제한
 * 
 * @param name 원본 이름
 * @returns 안전한 폴더명
 * 
 * @example
 * createSafeFolderName("TS24004-금형 개발") → "TS24004-금형_개발"
 * createSafeFolderName("Project #1 (Test)") → "Project_1_Test"
 */
export const createSafeFolderName = (name: string): string => {
  if (!name || name.trim() === '') {
    return 'unnamed';
  }

  return name
    .trim()
    .replace(/[^\w가-힣\s-]/g, '')  // 특수문자 제거 (한글, 영문, 숫자, 하이픈, 공백만 유지)
    .replace(/\s+/g, '_')            // 공백 → 언더스코어
    .replace(/_+/g, '_')             // 연속된 언더스코어 제거
    .replace(/^_|_$/g, '')           // 시작/끝 언더스코어 제거
    .substring(0, 100);              // 최대 100자
};

/**
 * Firebase 경로를 NAS 경로로 변환
 * 예: "molds/M001/photos/file.jpg" → "egmms/molds/M001/photos/file.jpg"
 */
export const firebasePathToNasPath = (firebasePath: string): string => {
  // 이미 'egmms/' prefix가 있으면 그대로 반환
  if (firebasePath.startsWith('egmms/')) {
    return firebasePath;
  }

  // 'egmms/' prefix 추가
  return `egmms/${firebasePath}`;
};

/**
 * NAS 경로를 Firebase 경로로 변환
 * 예: "egmms/molds/M001/photos/file.jpg" → "molds/M001/photos/file.jpg"
 */
export const nasPathToFirebasePath = (nasPath: string): string => {
  // 'egmms/' prefix 제거
  if (nasPath.startsWith('egmms/')) {
    return nasPath.substring(6); // 'egmms/'.length = 6
  }

  return nasPath;
};

/**
 * 경로에서 폴더 경로만 추출
 * 예: "molds/M001/images/file.jpg" → "molds/M001/images"
 */
export const getDirectoryPath = (filePath: string): string => {
  const parts = filePath.split('/');
  parts.pop(); // 마지막 파일명 제거
  return parts.join('/');
};

/**
 * 금형 ID에 대한 표준 폴더 구조 생성
 * 원본 계획서(NAS_Integration_Plan.md)에 따른 구조
 * 
 * @param moldId 금형 ID (Firestore 문서 ID)
 * @param moldNumber 금형 번호 (필수)
 * @param moldName 금형 이름 (선택적)
 * @returns 폴더 경로 배열
 * 
 * @example
 * getMoldFolderStructure("abc123", "M-001", "금형개발")
 * → ["egmms/molds/M-001_금형개발", ...]
 */
export const getMoldFolderStructure = (
  moldId: string, 
  moldNumber: string,
  moldName?: string
): string[] => {
  // 폴더명: 금형번호 또는 금형번호_금형명
  const folderName = moldName 
    ? `${createSafeFolderName(moldNumber)}_${createSafeFolderName(moldName)}`
    : createSafeFolderName(moldNumber);
  
  return [
    `egmms/molds/${folderName}`,
    `egmms/molds/${folderName}/drawings`,      // CAD 도면 (DWG, DXF)
    `egmms/molds/${folderName}/3d_models`,     // 3D 모델 (STEP, STL)
    `egmms/molds/${folderName}/photos`,        // 금형 사진
    `egmms/molds/${folderName}/thumbnails`,    // 썸네일
    `egmms/molds/${folderName}/documents`,     // 문서
    `egmms/molds/${folderName}/maintenance`,   // 유지보수 기록
    `egmms/molds/${folderName}/qr_codes`,      // QR 코드
    `egmms/molds/${folderName}/etc`,           // 기타 파일
  ];
};

/**
 * 프로젝트 ID에 대한 표준 폴더 구조 생성
 * 원본 계획서(NAS_Integration_Plan.md)에 따른 구조
 * 
 * @param projectId 프로젝트 ID (Firestore 문서 ID)
 * @param projectNumber 프로젝트 번호 (필수)
 * @param projectName 프로젝트 이름 (선택적)
 * @returns 폴더 경로 배열
 * 
 * @example
 * getProjectFolderStructure("xyz789", "TS24001", "신규프로젝트")
 * → ["egmms/projects/TS24001_신규프로젝트", ...]
 */
export const getProjectFolderStructure = (
  projectId: string,
  projectNumber: string,
  projectName?: string
): string[] => {
  // 폴더명: 프로젝트번호 또는 프로젝트번호_프로젝트명
  const folderName = projectName
    ? `${createSafeFolderName(projectNumber)}_${createSafeFolderName(projectName)}`
    : createSafeFolderName(projectNumber);
  
  return [
    `egmms/projects/${folderName}`,
    `egmms/projects/${folderName}/drawings`,      // 설계 도면 (DWG, DXF)
    `egmms/projects/${folderName}/3d_models`,     // 3D 모델 (STEP, STL)
    `egmms/projects/${folderName}/documents`,     // 문서 (PDF, XLSX)
    `egmms/projects/${folderName}/photos`,        // 프로젝트 사진
    `egmms/projects/${folderName}/references`,    // 참고자료
    `egmms/projects/${folderName}/ct_results`,    // CT 검사 결과
    `egmms/projects/${folderName}/etc`,           // 기타 파일
  ];
};

/**
 * Customer ID에 대한 표준 폴더 구조 생성
 */
export const getCustomerFolderStructure = (customerId: string): string[] => {
  return [
    `egmms/customers/${customerId}`,
    `egmms/customers/${customerId}/logos`,
    `egmms/customers/${customerId}/documents`,
  ];
};

/**
 * Location ID에 대한 표준 폴더 구조 생성
 */
export const getLocationFolderStructure = (locationId: string): string[] => {
  return [
    `egmms/locations/${locationId}`,
    `egmms/locations/${locationId}/images`,
    `egmms/locations/${locationId}/thumbnails`,
  ];
};

/**
 * 전체 기본 폴더 구조 생성
 */
export const getBaseFolderStructure = (): string[] => {
  return [
    'egmms',
    'egmms/molds',
    'egmms/customers',
    'egmms/locations',
    'egmms/projects',
    'egmms/temp',
  ];
};

/**
 * 파일 경로가 이미지인지 확인
 */
export const isImagePath = (path: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
  const ext = path.split('.').pop()?.toLowerCase();
  return ext ? imageExtensions.includes(ext) : false;
};

/**
 * 파일 경로가 3D 파일인지 확인
 */
export const is3DModelPath = (path: string): boolean => {
  const modelExtensions = ['stl', 'step', 'stp', 'obj', 'fbx', 'dae', 'gltf', 'glb'];
  const ext = path.split('.').pop()?.toLowerCase();
  return ext ? modelExtensions.includes(ext) : false;
};

/**
 * 파일 경로가 문서인지 확인
 */
export const isDocumentPath = (path: string): boolean => {
  const docExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];
  const ext = path.split('.').pop()?.toLowerCase();
  return ext ? docExtensions.includes(ext) : false;
};

/**
 * 썸네일 경로 생성
 * 예: "molds/M001/images/file.jpg" → "molds/M001/thumbnails/file_thumb.jpg"
 */
export const getThumbnailPath = (originalPath: string): string => {
  const dir = getDirectoryPath(originalPath);
  const fileName = originalPath.split('/').pop() || '';
  const baseName = fileName.replace(/\.[^/.]+$/, '');
  const ext = fileName.split('.').pop() || 'jpg';

  // images → thumbnails 변환
  const thumbnailDir = dir.replace('/images', '/thumbnails');
  
  return `${thumbnailDir}/${baseName}_thumb.${ext}`;
};

/**
 * 경로 정규화 (중복 슬래시 제거, 경로 정리)
 */
export const normalizePath = (path: string): string => {
  return path
    .replace(/\/+/g, '/') // 연속된 슬래시 제거
    .replace(/^\//, '') // 시작 슬래시 제거
    .replace(/\/$/, ''); // 끝 슬래시 제거
};

/**
 * WebDAV 절대 경로 생성
 * NAS 설정에서 공유 폴더명을 사용하여 전체 경로 생성
 */
export const buildWebDAVPath = (
  relativePath: string,
  shareFolder: string = 'egmms'
): string => {
  const normalizedPath = normalizePath(relativePath);
  
  // 이미 공유 폴더명이 포함되어 있으면 그대로 사용
  if (normalizedPath.startsWith(shareFolder)) {
    return `/${normalizedPath}`;
  }
  
  return `/${shareFolder}/${normalizedPath}`;
};

/**
 * 파일 크기를 사람이 읽기 쉬운 형식으로 변환
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * 파일명에서 타임스탬프 추출
 */
export const extractTimestampFromFilename = (filename: string): number | null => {
  const match = filename.match(/^(\d{13})_/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * 안전한 파일명 생성 (특수문자 제거)
 */
export const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_') // 특수문자를 언더스코어로 변경
    .replace(/_+/g, '_') // 연속된 언더스코어 제거
    .replace(/^_|_$/g, ''); // 시작/끝 언더스코어 제거
};

/**
 * 파일 카테고리를 NAS 폴더명으로 변환
 * file.types.ts의 FileCategory와 매핑
 */
export const getCategoryFolder = (category: string): string => {
  const categoryMap: Record<string, string> = {
    '설계도면': 'drawings',
    '3D모델': '3d_models',
    '문서': 'documents',
    '사진': 'photos',
    'CT결과서': 'ct_results',
    'QR코드': 'qr_codes',
    '기타': 'etc',  // 기타 전용 폴더
  };
  
  return categoryMap[category] || 'etc';  // 기본값: etc
};
